import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCustomerById, createCustomer, updateCustomer,
  getLoyaltyTransactions, redeemLoyaltyPoints, getWalletTransactions, adjustWallet,
  getCustomFieldDefinitions, getCustomFieldValues, setCustomFieldValues, getCustomFieldReferenceOptions,
  getNativeFieldLayout,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function AddEditCustomer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("customers");
  const { t: tCommon } = useTranslation("common");

  const [activeTab, setActiveTab] = useState("profile");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    is_active: true,
    is_tax_exempt: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // --- Custom Fields — admin-configured extra fields for the "customer"
  // page (see CustomFields.js and custom_field_handler.go). Definitions
  // are loaded regardless of create/edit mode (a brand-new customer can
  // fill them in before ever being saved); values are only loaded once an
  // id exists. customValues is keyed by definition id (as a string,
  // matching what the API already returns) and is always plain text —
  // "true"/"false" for a boolean field, the raw option string for a
  // dropdown, etc. — parsed/typed only at render and submit time.
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [customValues, setCustomValues] = useState({});

  // nativeFieldLayout is this page's own built-in fields (Name/Phone/...)
  // with whatever position/width the admin last set for them on the SAME
  // Custom Fields canvas (see CustomFields.js) — merged with
  // customFieldDefs below into one combined, sort_order-driven layout.
  // Seeded with this form's ORIGINAL hardcoded arrangement so the form
  // still renders correctly (and without a flash of missing fields)
  // before the real, possibly-customized layout has loaded.
  const [nativeFieldLayout, setNativeFieldLayout] = useState([
    { field_key: "name", label: t("field_name_label", "Customer Name"), width: 12, sort_order: 0 },
    { field_key: "phone", label: t("field_phone_label", "Phone (optional)"), width: 6, sort_order: 1 },
    { field_key: "email", label: t("field_email_label", "Email (optional)"), width: 6, sort_order: 2 },
    { field_key: "address", label: t("field_address_label", "Address (optional)"), width: 12, sort_order: 3 },
    { field_key: "is_active", label: tCommon("status_active", "Active"), width: 12, sort_order: 4 },
    { field_key: "is_tax_exempt", label: "Tax Exempt", width: 12, sort_order: 5 },
  ]);
  useEffect(() => {
    getNativeFieldLayout(token, "customer").then((rows) => { if (rows && rows.length) setNativeFieldLayout(rows); }).catch(() => {});
  }, [token]);

  // tableOptionsByDefId holds the live-resolved {value,label} choices for
  // every "select" field whose options come from an existing table (e.g.
  // Countries) rather than a fixed manual list — see CustomFields.js's
  // "Options Source" setting and GetCustomFieldReferenceOptionsHandler.
  const [tableOptionsByDefId, setTableOptionsByDefId] = useState({});

  useEffect(() => {
    getCustomFieldDefinitions(token, "customer").then((defs) => setCustomFieldDefs(defs || [])).catch(() => {});
  }, [token]);

  // An ordinary table-sourced field's options never change once loaded —
  // fetched once per definitions load.
  useEffect(() => {
    customFieldDefs
      .filter((d) => d.field_type === "select" && d.options_source === "table" && !d.depends_on_definition_id)
      .forEach((d) => {
        getCustomFieldReferenceOptions(token, d.id)
          .then((opts) => setTableOptionsByDefId((prev) => ({ ...prev, [d.id]: opts || [] })))
          .catch(() => {});
      });
  }, [customFieldDefs, token]);

  // A cascading dependent field (see CustomFields.js's "Depends on field")
  // must re-fetch its options every time the PARENT field's own current
  // value changes on this very form — e.g. picking a different Country
  // live-narrows a Zones field beneath it. dependentFieldsKey is a plain
  // string built from just the parent values these fields actually care
  // about, so this effect only reruns when one of THOSE changes, not on
  // every unrelated keystroke elsewhere on the form.
  const dependentFieldsKey = customFieldDefs
    .filter((d) => d.field_type === "select" && d.options_source === "table" && d.depends_on_definition_id)
    .map((d) => `${d.id}:${customValues[d.depends_on_definition_id] ?? ""}`)
    .join("|");

  useEffect(() => {
    customFieldDefs
      .filter((d) => d.field_type === "select" && d.options_source === "table" && d.depends_on_definition_id)
      .forEach((d) => {
        const parentValue = customValues[d.depends_on_definition_id];
        getCustomFieldReferenceOptions(token, d.id, parentValue)
          .then((opts) => setTableOptionsByDefId((prev) => ({ ...prev, [d.id]: opts || [] })))
          .catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldDefs, token, dependentFieldsKey]);

  // Once a dependent field's options are refreshed for its parent's
  // CURRENT value, drop any already-selected choice(s) that no longer
  // appear in that list — e.g. a previously-picked Zone that doesn't
  // belong to the newly-selected Country. Skipped entirely while the
  // parent has no value yet, so this never touches a field's saved value
  // during the brief window before the real parent value has loaded.
  useEffect(() => {
    customFieldDefs
      .filter((d) => d.field_type === "select" && d.options_source === "table" && d.depends_on_definition_id)
      .forEach((d) => {
        const parentValue = customValues[d.depends_on_definition_id];
        if (!parentValue) return;
        const options = tableOptionsByDefId[d.id];
        if (!options) return;
        const validValues = new Set(options.map((o) => String(o.value)));
        setCustomValues((prev) => {
          const current = prev[d.id];
          if (current === undefined) return prev;
          if (d.is_multi) {
            let arr;
            try { arr = JSON.parse(current || "[]"); } catch { arr = []; }
            const filtered = arr.filter((v) => validValues.has(String(v)));
            if (filtered.length === arr.length) return prev;
            return { ...prev, [d.id]: JSON.stringify(filtered) };
          }
          if (!current || validValues.has(String(current))) return prev;
          return { ...prev, [d.id]: "" };
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableOptionsByDefId, customFieldDefs]);

  useEffect(() => {
    if (!id) return;
    getCustomFieldValues(token, "customer", id).then((vals) => setCustomValues(vals || {})).catch(() => {});
  }, [id, token]);

  // A boolean custom field always needs an explicit "true"/"false" entry
  // so its ToggleSwitch has a defined checked state from the moment it
  // first renders (same reasoning for a multi-select's "[]") — on a
  // brand-new customer as much as an existing one whose value was never
  // touched.
  useEffect(() => {
    setCustomValues((prev) => {
      let changed = false;
      const next = { ...prev };
      customFieldDefs.forEach((d) => {
        if (d.field_type === "boolean" && next[d.id] === undefined) {
          next[d.id] = "false";
          changed = true;
        } else if (d.field_type === "select" && d.is_multi && next[d.id] === undefined) {
          next[d.id] = "[]";
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [customFieldDefs]);

  // Mirrors custom_field_handler.go's own isCustomFieldValueEmpty: an
  // empty multi-select array counts as "not filled in" the same way a
  // blank string does for every other field type.
  const isCustomValueEmpty = (def, raw) => {
    if (def.field_type === "select" && def.is_multi) {
      try {
        return JSON.parse(raw || "[]").length === 0;
      } catch {
        return true;
      }
    }
    return !String(raw ?? "").trim();
  };

  const setCustomValue = (defId, value) => {
    setCustomValues((prev) => ({ ...prev, [defId]: value }));
  };

  // --- Loyalty points + wallet — only relevant once the customer already
  // exists (a brand-new customer has never earned or spent anything yet).
  // `balances` is a light refetch of just loyalty_points/wallet_balance
  // (via getCustomerById) after every redeem/adjust, kept separate from
  // `form` above so those two actions never touch the profile-edit form
  // state at all.
  const [balances, setBalances] = useState({ loyalty_points: 0, wallet_balance: 0 });
  const [loyaltyTx, setLoyaltyTx] = useState([]);
  const [walletTx, setWalletTx] = useState([]);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);

  const loadLoyaltyData = () => {
    if (!id) return;
    getCustomerById(token, id).then((cust) => {
      setBalances({ loyalty_points: cust.loyalty_points || 0, wallet_balance: cust.wallet_balance || 0 });
    }).catch(() => {});
    getLoyaltyTransactions(token, id).then(setLoyaltyTx).catch(() => {});
    getWalletTransactions(token, id).then(setWalletTx).catch(() => {});
  };

  useEffect(() => {
    if (id) {
      getCustomerById(token, id).then((cust) => {
        setForm({
          name: cust.name || "",
          phone: cust.phone || "",
          email: cust.email || "",
          address: cust.address || "",
          is_active: cust.is_active !== undefined ? cust.is_active : true,
          is_tax_exempt: cust.is_tax_exempt || false,
        });
        setBalances({ loyalty_points: cust.loyalty_points || 0, wallet_balance: cust.wallet_balance || 0 });
      }).catch(() => notify.error(t("notify_load_failed", "Failed to load customer")));
      getLoyaltyTransactions(token, id).then(setLoyaltyTx).catch(() => {});
      getWalletTransactions(token, id).then(setWalletTx).catch(() => {});
    }
  }, [id, token]);

  const handleRedeem = async () => {
    setRedeeming(true);
    try {
      await redeemLoyaltyPoints(token, id, redeemPoints === "" ? 0 : Number(redeemPoints));
      notify.success(t("notify_points_redeemed", "Points redeemed into wallet balance"));
      setShowRedeemModal(false);
      setRedeemPoints("");
      loadLoyaltyData();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_redeem_failed", "Failed to redeem points"));
    } finally {
      setRedeeming(false);
    }
  };

  const handleAdjustWallet = async () => {
    if (!adjustAmount || Number(adjustAmount) === 0) {
      notify.error(t("validation_nonzero_amount", "Enter a non-zero amount"));
      return;
    }
    setAdjusting(true);
    try {
      await adjustWallet(token, id, { amount: Number(adjustAmount), reason: adjustReason.trim() });
      notify.success(t("notify_wallet_adjusted", "Wallet balance adjusted"));
      setShowAdjustModal(false);
      setAdjustAmount("");
      setAdjustReason("");
      loadLoyaltyData();
    } catch (err) {
      notify.error(err.data?.error || err.message || t("notify_adjust_failed", "Failed to adjust wallet balance"));
    } finally {
      setAdjusting(false);
    }
  };

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    setErrors({});
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("validation_name_required", "Customer name is required");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    const missingCustom = customFieldDefs.find(
      (d) => d.is_required && d.field_type !== "boolean" && isCustomValueEmpty(d, customValues[d.id])
    );
    if (missingCustom) {
      notify.error(t("validation_custom_field_required", "\"{label}\" is required").replace("{label}", missingCustom.label));
      return;
    }

    setLoading(true);
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      is_active: form.is_active,
      is_tax_exempt: form.is_tax_exempt,
    };

    try {
      let customerId = id;
      if (id) {
        await updateCustomer(token, id, payload);
      } else {
        const created = await createCustomer(token, payload);
        customerId = created.id;
      }
      if (customFieldDefs.length > 0) {
        // customerId is a route param (always a string) when editing an
        // existing customer — the backend's EntityID is a Go uint, so a
        // string here fails to unmarshal ("Invalid JSON format") instead
        // of just being coerced. Number(...) always before sending.
        await setCustomFieldValues(token, "customer", Number(customerId), customValues);
      }
      notify.success(id ? t("notify_updated", "Customer updated successfully") : t("notify_created", "Customer created successfully"));
      navigate("/customers");
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  const renderCustomField = (def) => {
    const value = customValues[def.id] ?? "";
    const label = def.label + (def.is_required ? " *" : "");

    // A "select" field's real {value,label} choices — either the fixed
    // manual list stored right on the definition, or the live list
    // resolved from a reference table (see tableOptionsByDefId above),
    // which starts out empty for the instant before that fetch resolves.
    let selectOptions = [];
    if (def.field_type === "select") {
      if (def.options_source === "table") {
        selectOptions = tableOptionsByDefId[def.id] || [];
      } else {
        try {
          selectOptions = (JSON.parse(def.options || "[]")).map((o) => ({ value: o, label: o }));
        } catch {
          selectOptions = [];
        }
      }
    }

    // A cascading dependent field has nothing sensible to offer until its
    // parent field actually has a value — disable it and say so, rather
    // than showing an empty-looking dropdown with no explanation.
    const parentDef = def.depends_on_definition_id
      ? customFieldDefs.find((d) => d.id === def.depends_on_definition_id)
      : null;
    const waitingOnParent = !!parentDef && isCustomValueEmpty(parentDef, customValues[parentDef.id]);

    return (
      <div key={def.id} style={{ gridColumn: `span ${def.width || 12} / span ${def.width || 12}` }}>
        {def.field_type === "boolean" ? (
          <ToggleSwitch label={label} checked={value === "true"} onChange={(val) => setCustomValue(def.id, val ? "true" : "false")} />
        ) : def.field_type === "select" && def.is_multi ? (
          <SelectBox
            label={label}
            isMulti
            isDisabled={waitingOnParent}
            options={selectOptions}
            value={(() => { try { return JSON.parse(value || "[]"); } catch { return []; } })()}
            onChange={(vals) => setCustomValue(def.id, JSON.stringify(vals || []))}
          />
        ) : def.field_type === "select" ? (
          <SelectBox
            label={label}
            isDisabled={waitingOnParent}
            options={selectOptions}
            value={value}
            onChange={(val) => setCustomValue(def.id, val)}
          />
        ) : (
          <FloatingInput
            label={label}
            type={def.field_type === "number" ? "number" : def.field_type === "date" ? "date" : "text"}
            value={value}
            onChange={(e) => setCustomValue(def.id, e.target.value)}
          />
        )}
        {waitingOnParent && (
          <p className="text-[10px] text-gray-400 mt-1 ms-2">{t("custom_field_waiting_hint", "Pick a {label} first").replace("{label}", parentDef.label)}</p>
        )}
      </div>
    );
  };

  // NATIVE_FIELD_INPUTS renders this form's own BUILT-IN fields — their
  // label text, validation, and component choice are exactly what they
  // always were; only WHERE they sit and how wide they are is now driven
  // by nativeFieldLayout (see CustomFields.js's shared layout canvas)
  // instead of being hardcoded into the JSX below.
  const NATIVE_FIELD_INPUTS = {
    name: () => <FloatingInput label={t("field_name_label", "Customer Name")} value={form.name} onChange={setField("name")} error={errors.name} icon={ICONS.Customers} />,
    phone: () => <FloatingInput label={t("field_phone_label", "Phone (optional)")} value={form.phone} onChange={setField("phone")} />,
    email: () => <FloatingInput label={t("field_email_label", "Email (optional)")} value={form.email} onChange={setField("email")} />,
    address: () => <FloatingInput label={t("field_address_label", "Address (optional)")} value={form.address} onChange={setField("address")} />,
    is_active: () => (
      <ToggleSwitch label={tCommon("status_active", "Active")} checked={form.is_active} onChange={(val) => setForm((prev) => ({ ...prev, is_active: val }))} />
    ),
    is_tax_exempt: () => (
      <ToggleSwitch
        label={t("toggle_tax_exempt", "Tax Exempt (e.g. holds an exemption certificate, or an export sale)")}
        checked={form.is_tax_exempt}
        onChange={(val) => setForm((prev) => ({ ...prev, is_tax_exempt: val }))}
      />
    ),
  };

  const renderNativeField = (item) => (
    <div key={`native-${item.field_key}`} style={{ gridColumn: `span ${item.width || 12} / span ${item.width || 12}` }}>
      {NATIVE_FIELD_INPUTS[item.field_key]?.()}
    </div>
  );

  // The single, unified arrangement this page actually renders — built-in
  // fields and real Custom Fields interleaved by the SAME sort_order space
  // (see CustomFields.js's "Save Layout", which writes both from one
  // combined canvas), so admin control over the form's layout is total.
  const profileLayout = [
    ...nativeFieldLayout.map((n) => ({ ...n, kind: "native" })),
    ...customFieldDefs.map((d) => ({ ...d, kind: "custom" })),
  ].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/customers")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{id ? t("edit_title", "Edit Customer") : t("add_title", "Add New Customer")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("page_subtitle", "Optional on every sale — checkout works fine without one")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        {id && (
          <div className="flex gap-2 mb-6 border-b border-gray-100">
            <button
              type="button"
              onClick={() => setActiveTab("profile")}
              className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${activeTab === "profile" ? "border-[var(--primary-color)] text-[var(--primary-color)]" : "border-transparent text-gray-400 hover:text-gray-600"}`}
            >
              {t("tab_profile", "Profile")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("loyalty")}
              className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition-colors ${activeTab === "loyalty" ? "border-[var(--primary-color)] text-[var(--primary-color)]" : "border-transparent text-gray-400 hover:text-gray-600"}`}
            >
              {t("tab_loyalty", "Loyalty & Wallet")}
            </button>
          </div>
        )}

        {activeTab === "profile" && (
          <div className="space-y-6">
            {/* Built-in fields (Name/Phone/Email/...) and real Custom
                Fields render together here, in whatever single
                arrangement was set up on the Custom Fields admin page —
                see profileLayout above. */}
            <div className="grid grid-cols-12 gap-4">
              {profileLayout.map((item) => (item.kind === "native" ? renderNativeField(item) : renderCustomField(item)))}
            </div>

            <div className="flex gap-4 pt-6 border-t border-gray-50">
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={loading}
                className="flex-grow shadow-lg shadow-blue-500/10"
                icon={id ? ICONS.Save : ICONS.Plus}
              >
                {id ? t("btn_update_customer", "Update Customer") : t("btn_create_customer", "Create Customer")}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/customers")}>{tCommon("btn_cancel", "Cancel")}</Button>
            </div>
          </div>
        )}

        {activeTab === "loyalty" && id && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-2xl p-4">
                <p className="text-gray-400 text-xs font-medium mb-1">{t("col_loyalty_points", "Loyalty Points")}</p>
                <p className="text-2xl font-black text-gray-800">{balances.loyalty_points}</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-4">
                <p className="text-gray-400 text-xs font-medium mb-1">{t("col_wallet_balance", "Wallet Balance")}</p>
                <p className="text-2xl font-black text-gray-800">{Number(balances.wallet_balance || 0).toFixed(2)}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button variant="secondary" onClick={() => setShowRedeemModal(true)} className="flex-1">
                {t("btn_redeem_points", "Redeem Points")}
              </Button>
              <Button variant="secondary" onClick={() => setShowAdjustModal(true)} className="flex-1">
                {t("btn_adjust_wallet", "Adjust Wallet")}
              </Button>
            </div>

            {(loyaltyTx.length > 0 || walletTx.length > 0) && (
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <p className="text-xs font-bold text-gray-400 mb-2">{t("recent_point_activity_label", "Recent Point Activity")}</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {loyaltyTx.slice(0, 10).map((tx) => (
                      <div key={tx.id} className="flex justify-between text-xs text-gray-500 bg-white border border-gray-100 rounded-lg px-2 py-1">
                        <span className="capitalize">{tx.type.replace("_", " ")}</span>
                        <span className={tx.points >= 0 ? "text-green-600 font-semibold" : "text-red-500 font-semibold"}>
                          {tx.points >= 0 ? `+${tx.points}` : tx.points}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 mb-2">{t("recent_wallet_activity_label", "Recent Wallet Activity")}</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {walletTx.slice(0, 10).map((tx) => (
                      <div key={tx.id} className="flex justify-between text-xs text-gray-500 bg-white border border-gray-100 rounded-lg px-2 py-1">
                        <span className="capitalize">{tx.type.replace("_", " ")}</span>
                        <span className={tx.amount >= 0 ? "text-green-600 font-semibold" : "text-red-500 font-semibold"}>
                          {tx.amount >= 0 ? `+${tx.amount.toFixed(2)}` : tx.amount.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showRedeemModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-gray-800 mb-1">{t("redeem_modal_title", "Redeem Points")}</h3>
            <p className="text-gray-400 text-xs mb-4">
              {t("redeem_modal_hint", "Current balance: {points} points. Leave blank to redeem everything.").replace("{points}", balances.loyalty_points)}
            </p>
            <FloatingInput
              label={t("field_points_to_redeem_label", "Points to Redeem (optional)")}
              type="number"
              min="0"
              value={redeemPoints}
              onChange={(e) => setRedeemPoints(e.target.value)}
            />
            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowRedeemModal(false)} className="flex-1" disabled={redeeming}>
                {tCommon("btn_cancel", "Cancel")}
              </Button>
              <Button variant="primary" onClick={handleRedeem} loading={redeeming} className="flex-1">
                {t("btn_redeem", "Redeem")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAdjustModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-gray-800 mb-1">{t("adjust_modal_title", "Adjust Wallet Balance")}</h3>
            <p className="text-gray-400 text-xs mb-4">
              {t("adjust_modal_hint", "Current balance: {balance}. Use a negative amount to deduct.").replace("{balance}", Number(balances.wallet_balance || 0).toFixed(2))}
            </p>
            <div className="space-y-4">
              <FloatingInput
                label={t("field_amount_label", "Amount (+ credit / - deduct)")}
                type="number"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
              />
              <FloatingInput
                label={t("field_reason_label", "Reason (optional)")}
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
              />
            </div>
            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={() => setShowAdjustModal(false)} className="flex-1" disabled={adjusting}>
                {tCommon("btn_cancel", "Cancel")}
              </Button>
              <Button variant="primary" onClick={handleAdjustWallet} loading={adjusting} className="flex-1">
                {t("btn_apply", "Apply")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
